/**
 * Standard empty-state placeholder — dashed border, centred icon
 * label, optional CTA. Replaces the half-dozen near-identical "no
 * X yet" boxes scattered across the app, each with slightly
 * different padding, font sizes, and icon styling.
 *
 * `art` lets a caller swap the icon for a small schematic-style
 * SVG (board outline, BOM grid, etc.) so the empty state speaks
 * the brand's hardware vocabulary instead of generic Material
 * iconography.
 */
import { ICON_SIZE, blueprintBg } from "@/theme/styleHelpers";
import { Box, Button, Stack, Typography, alpha } from "@mui/material";
import type { ElementType, ReactNode } from "react";

interface EmptyStateProps {
  icon?: ElementType;
  /**
   * Optional SVG / illustration slot. Takes precedence over `icon`.
   * Pass a small (~64-80px) decorative element. The component frames
   * it inside a soft brand-tinted plate.
   */
  art?: ReactNode;
  /** Primary one-line message. */
  label: string;
  /** Secondary line for context — short, grey. */
  hint?: string;
  /** Optional CTA — pass a fully-formed Button or its props. */
  cta?: ReactNode | { label: string; onClick: () => void };
  /** Tighten the surrounding padding for inline (per-row) empty states. */
  dense?: boolean;
}

export function EmptyState({
  icon: Icon,
  art,
  label,
  hint,
  cta,
  dense = false,
}: EmptyStateProps) {
  const ctaNode = !cta ? null : typeof cta === "object" &&
    cta !== null &&
    "label" in cta ? (
    <Button size="small" variant="outlined" onClick={cta.onClick}>
      {cta.label}
    </Button>
  ) : (
    (cta as ReactNode)
  );

  // The `art` slot wins over `icon`; either renders nothing if both
  // are absent so callers can compose their own header.
  const visual = art ? (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        // Soft brand-tinted plate around the artwork — the same
        // recipe FeatureCard uses for its icon plate. Reads as
        // "this is where the content goes" without screaming for
        // attention.
        width: dense ? 44 : 56,
        height: dense ? 44 : 56,
        borderRadius: 1.5,
        bgcolor: "accent.softer",
        border: "1px solid",
        borderColor: "border.accent",
        mb: 0.5,
        // Give the SVG a faint primary-tint default; callers can
        // override via the SVG's own fill / stroke.
        color: "primary.light",
      }}
    >
      {art}
    </Box>
  ) : Icon ? (
    <Icon
      sx={{
        fontSize: dense ? ICON_SIZE.section : 36,
        color: "text.disabled",
        opacity: 0.6,
      }}
    />
  ) : null;

  return (
    <Box
      sx={{
        border: "1px dashed",
        borderColor: "border.default",
        borderRadius: 1,
        p: dense ? 1.75 : 3,
        textAlign: "center",
        bgcolor: "transparent",
        // Faint corner accent — a single PCB trace tucking into the
        // top-left, reinforcing the "ready for input" read without
        // adding clutter. Drawn with a CSS gradient so it stays
        // crisp at any size.
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: 32,
          height: 32,
          borderTop: "1px solid",
          borderLeft: "1px solid",
          borderColor: (t) => alpha(t.palette.primary.main, 0.35),
          borderTopLeftRadius: 4,
          pointerEvents: "none",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          right: 0,
          width: 32,
          height: 32,
          borderBottom: "1px solid",
          borderRight: "1px solid",
          borderColor: (t) => alpha(t.palette.primary.main, 0.35),
          borderBottomRightRadius: 4,
          pointerEvents: "none",
        },
      }}
    >
      <Stack spacing={1} alignItems="center" sx={{ position: "relative" }}>
        {visual}
        <Typography
          variant="body2"
          sx={{ color: "text.disabled", fontSize: "0.8125rem" }}
        >
          {label}
        </Typography>
        {hint && (
          <Typography
            variant="caption"
            sx={{ color: "text.disabled", maxWidth: 320 }}
          >
            {hint}
          </Typography>
        )}
        {ctaNode}
      </Stack>
    </Box>
  );
}

/**
 * Schematic-style "empty board" sketch — a faint PCB outline with
 * pad anchors at the corners. The default `art` for project-level
 * empty states ("no PCBs", "no versions").
 */
export function EmptyBoardArt() {
  return (
    <Box
      component="svg"
      viewBox="0 0 32 32"
      sx={{ width: 28, height: 28, color: "primary.light" }}
      aria-hidden
    >
      <rect
        x="5"
        y="5"
        width="22"
        height="22"
        rx="2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
        strokeOpacity="0.65"
      />
      <circle cx="9" cy="9" r="1.4" fill="currentColor" fillOpacity="0.7" />
      <circle cx="23" cy="9" r="1.4" fill="currentColor" fillOpacity="0.7" />
      <circle cx="9" cy="23" r="1.4" fill="currentColor" fillOpacity="0.7" />
      <circle cx="23" cy="23" r="1.4" fill="currentColor" fillOpacity="0.7" />
      <path
        d="M9 9 L23 9 M9 23 L23 23 M9 9 L9 23"
        stroke="currentColor"
        strokeOpacity="0.3"
        strokeWidth="0.8"
        fill="none"
        strokeDasharray="2 2"
      />
    </Box>
  );
}

/**
 * BlueprintEmptyState — the larger brand-plate empty state for hero
 * surfaces (CircuitsTab, ProjectsPage, HistoryPage, DRC clean). Bigger
 * blueprint-grid backdrop, phosphor-stroked motif, "READY FOR INPUT"
 * eyebrow, headline + body + CTA. Designed to make a barren page feel
 * intentionally designed instead of half-finished.
 *
 * Reach for `EmptyState` (above) for inline / per-row emptiness;
 * reach for `BlueprintEmptyState` for whole-page emptiness.
 */
export function BlueprintEmptyState({
  motif,
  eyebrow = "READY FOR INPUT",
  headline,
  body,
  ctaLabel,
  onCtaClick,
  secondaryLabel,
  onSecondaryClick,
  compact = false,
}: {
  /** Inline SVG children (paths / circles / lines) inside an 80×80 viewBox.
   *  The chassis adds the SVG wrapper, sizing, phosphor stroke, and
   *  drop-shadow glow. */
  motif: ReactNode;
  eyebrow?: string;
  headline: string;
  body?: string;
  ctaLabel?: string;
  onCtaClick?: () => void;
  secondaryLabel?: string;
  onSecondaryClick?: () => void;
  /** Tighter density for placeholder surfaces that share their viewport
   *  with a page header (the project Inspect tabs). Halves the vertical
   *  padding and drops the motif from 80px to 56px so the placeholder
   *  fits inside a typical laptop viewport without scrolling. */
  compact?: boolean;
}) {
  const motifSize = compact ? 56 : 80;
  return (
    <Box
      sx={(t) => ({
        ...blueprintBg(t),
        borderRadius: 2,
        border: `1px solid ${t.palette.border.default}`,
        py: compact ? { xs: 3, sm: 4 } : { xs: 6, sm: 8 },
        px: 4,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: compact ? 1.5 : 2.5,
      })}
    >
      <Box
        component="svg"
        viewBox="0 0 80 80"
        width={motifSize}
        height={motifSize}
        fill="none"
        sx={(t) => ({
          stroke: t.palette.accent.main,
          strokeWidth: 1.5,
          strokeLinecap: "round",
          strokeLinejoin: "round",
          opacity: 0.85,
          filter: `drop-shadow(0 0 8px ${alpha(t.palette.accent.main, 0.35)})`,
        })}
      >
        {motif}
      </Box>
      <Box>
        <Typography
          variant="overline"
          sx={(t) => ({
            color: t.palette.editorial.main,
            fontSize: 10,
            letterSpacing: "0.18em",
          })}
        >
          {eyebrow}
        </Typography>
        <Typography
          variant="h6"
          sx={{ mt: 0.5, color: "text.primary", fontWeight: 600 }}
        >
          {headline}
        </Typography>
        {body && (
          <Typography
            variant="body2"
            sx={{
              mt: 0.75,
              color: "text.secondary",
              maxWidth: 380,
              mx: "auto",
            }}
          >
            {body}
          </Typography>
        )}
      </Box>
      {(ctaLabel || secondaryLabel) && (
        <Stack direction="row" spacing={1.5}>
          {ctaLabel && onCtaClick && (
            <Button variant="contained" onClick={onCtaClick}>
              {ctaLabel}
            </Button>
          )}
          {secondaryLabel && onSecondaryClick && (
            <Button variant="text" onClick={onSecondaryClick}>
              {secondaryLabel}
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
}

// Stock motifs — reusable phosphor-stroked SVG primitives. Add new
// motifs here rather than ad-libbing per surface so the language stays
// consistent.
export const ResistorMotif = (
  <>
    <line x1="8" y1="40" x2="20" y2="40" />
    <polyline points="20,40 26,28 38,52 50,28 62,52 68,40" />
    <line x1="68" y1="40" x2="80" y2="40" />
  </>
);

export const ConnectorMotif = (
  <>
    <rect x="14" y="22" width="52" height="36" rx="3" />
    <line x1="22" y1="30" x2="22" y2="50" />
    <line x1="30" y1="30" x2="30" y2="50" />
    <line x1="40" y1="30" x2="40" y2="50" />
    <line x1="50" y1="30" x2="50" y2="50" />
    <line x1="58" y1="30" x2="58" y2="50" />
  </>
);

export const ConstellationMotif = (
  <>
    <circle cx="20" cy="22" r="6" />
    <circle cx="60" cy="22" r="6" />
    <circle cx="40" cy="58" r="6" />
    <path d="M 26 22 L 54 22" />
    <path d="M 22 28 L 36 54" />
    <path d="M 58 28 L 44 54" />
  </>
);

export const ClockMotif = (
  <>
    <circle cx="40" cy="40" r="28" />
    <line x1="40" y1="40" x2="40" y2="22" />
    <line x1="40" y1="40" x2="56" y2="44" />
    <circle cx="40" cy="40" r="2" fill="currentColor" />
  </>
);

export const CheckMotif = (
  <>
    <circle cx="40" cy="40" r="28" />
    <polyline points="26,42 36,52 56,30" />
  </>
);

export const PenMotif = (
  <>
    <path d="M 18 62 L 26 54 L 60 20 L 68 28 L 34 62 L 26 62 Z" />
    <line x1="54" y1="26" x2="62" y2="34" />
  </>
);

/** PCB board outline with mounting-hole pads and a few traces — the
 *  default motif for the "no PCB layer in this version" empty state. */
export const PcbBoardMotif = (
  <>
    <rect x="14" y="14" width="52" height="52" rx="3" />
    <circle cx="22" cy="22" r="2.5" />
    <circle cx="58" cy="22" r="2.5" />
    <circle cx="22" cy="58" r="2.5" />
    <circle cx="58" cy="58" r="2.5" />
    <path d="M 30 32 L 50 32 L 50 48 L 30 48 Z" />
    <line x1="22" y1="40" x2="30" y2="40" />
    <line x1="50" y1="40" x2="58" y2="40" />
    <line x1="40" y1="14" x2="40" y2="32" strokeDasharray="2 2" />
    <line x1="40" y1="48" x2="40" y2="66" strokeDasharray="2 2" />
  </>
);

/**
 * Schematic-style "empty BOM" grid — three rows of dashed cells,
 * suggesting a parts table waiting for content.
 */
export function EmptyBomArt() {
  return (
    <Box
      component="svg"
      viewBox="0 0 32 32"
      sx={{ width: 28, height: 28, color: "primary.light" }}
      aria-hidden
    >
      <g
        stroke="currentColor"
        strokeOpacity="0.55"
        strokeWidth="1"
        fill="none"
        strokeDasharray="2 1.5"
      >
        <rect x="5" y="7" width="22" height="5" rx="1" />
        <rect x="5" y="14" width="22" height="5" rx="1" />
        <rect x="5" y="21" width="22" height="5" rx="1" />
      </g>
      <line
        x1="13"
        y1="7"
        x2="13"
        y2="26"
        stroke="currentColor"
        strokeOpacity="0.3"
        strokeWidth="0.6"
      />
      <line
        x1="20"
        y1="7"
        x2="20"
        y2="26"
        stroke="currentColor"
        strokeOpacity="0.3"
        strokeWidth="0.6"
      />
    </Box>
  );
}
